import { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, SearchX, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandInput,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';
import { OrderProductIdentity, type OrderProduct } from './order-product-result';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSearch: string;
  items: { productId?: string; quantity: number }[];
  addingId: string | null;
  announcement: string;
  onAdd: (product: OrderProduct) => Promise<void>;
}

export function OrderProductCatalog({
  open,
  onOpenChange,
  initialSearch,
  items,
  addingId,
  announcement,
  onAdd,
}: Props) {
  const { t } = useTranslation('trading');
  const [search, setSearch] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch.trim());
  const [brandId, setBrandId] = useState('');
  const [modelId, setModelId] = useState('');
  const [inStock, setInStock] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const facets = trpc.tradingProduct.pickerFilters.useQuery(undefined, {
    enabled: open,
    staleTime: 60_000,
  });
  const brands = facets.data ?? [];
  const models = brands.find((brand) => brand.id === brandId)?.models ?? [];
  const query = trpc.tradingProduct.list.useInfiniteQuery(
    {
      search: debouncedSearch || undefined,
      brandId: brandId || undefined,
      modelId: modelId || undefined,
      inStock: inStock || undefined,
      includeInactive: false,
      limit: 24,
    },
    { enabled: open, staleTime: 30_000, getNextPageParam: (page) => page.nextCursor ?? undefined }
  );
  const products = query.data?.pages.flatMap((page) => page.products) ?? [];
  const total = query.data?.pages[0]?.pagination.total ?? 0;
  const loading =
    (query.isFetching && !query.isFetchingNextPage) || search.trim() !== debouncedSearch;
  const filtered = !!search || !!brandId || !!modelId || inStock;
  const resetFilters = () => {
    setSearch('');
    setBrandId('');
    setModelId('');
    setInStock(false);
    inputRef.current?.focus();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[min(760px,calc(100dvh-2rem))] w-[calc(100%-2rem)] max-w-4xl flex-col gap-0 overflow-hidden p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <DialogHeader className="border-b p-4 pr-12 text-left sm:p-5">
          <DialogTitle className="font-sans">{t('sales.orders.catalogTitle')}</DialogTitle>
          <DialogDescription>{t('sales.orders.catalogHelp')}</DialogDescription>
        </DialogHeader>
        <Command
          shouldFilter={false}
          label={t('sales.orders.catalogTitle')}
          className="min-h-0 flex-1"
        >
          <div className="shrink-0 space-y-3 border-b p-4 sm:px-5">
            <div className="border-input focus-within:border-foreground relative border [&_[cmdk-input-wrapper]]:border-0">
              <CommandInput
                ref={inputRef}
                value={search}
                onValueChange={setSearch}
                placeholder={t('sales.orders.productSearchPlaceholder')}
                className="h-11 pr-8"
              />
              {search && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="absolute right-1 top-1.5"
                  aria-label={t('sales.orders.clearSearch')}
                  onKeyDown={(event) => event.stopPropagation()}
                  onClick={() => {
                    setSearch('');
                    inputRef.current?.focus();
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div
              className="grid grid-cols-2 gap-3 sm:grid-cols-[1fr_1fr_auto]"
              onKeyDown={(event) => event.stopPropagation()}
            >
              <div className="min-w-0 space-y-1.5">
                <Label className="text-xs">{t('products.brand')}</Label>
                <Combobox
                  ariaLabel={t('products.brand')}
                  value={brandId}
                  onValueChange={(value) => {
                    setBrandId(value);
                    setModelId('');
                  }}
                  options={[
                    { value: '', label: t('products.allBrands') },
                    ...brands.map((brand) => ({
                      value: brand.id,
                      label: brand.name,
                      description: brand.code,
                      keywords: [brand.code],
                    })),
                  ]}
                  placeholder={
                    facets.isFetching ? t('sales.orders.loadingFilters') : t('products.allBrands')
                  }
                  searchPlaceholder={t('sales.orders.searchBrands')}
                  emptyText={t('sales.orders.noFilterResults')}
                  disabled={!brands.length}
                  className="h-10"
                />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label className="text-xs">{t('products.model')}</Label>
                <Combobox
                  ariaLabel={t('products.model')}
                  value={modelId}
                  onValueChange={setModelId}
                  options={[
                    {
                      value: '',
                      label: t(brandId ? 'products.allModels' : 'sales.orders.selectBrandFirst'),
                    },
                    ...models.map((model) => ({
                      value: model.id,
                      label: model.name,
                      description: model.code,
                      keywords: [model.code],
                    })),
                  ]}
                  placeholder={
                    brandId ? t('products.allModels') : t('sales.orders.selectBrandFirst')
                  }
                  searchPlaceholder={t('sales.orders.searchModels')}
                  emptyText={t('sales.orders.noFilterResults')}
                  disabled={!brandId}
                  className="h-10"
                />
              </div>
              <label className="col-span-2 flex cursor-pointer items-center gap-2 text-sm sm:col-span-1 sm:self-end sm:py-3">
                <Checkbox
                  checked={inStock}
                  onCheckedChange={(checked) => setInStock(checked === true)}
                />
                {t('sales.orders.onlyAvailable')}
              </label>
            </div>
            {facets.isError && (
              <div role="alert" className="text-destructive flex items-center gap-2 text-xs">
                {t('sales.orders.filtersError')}
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onKeyDown={(event) => event.stopPropagation()}
                  onClick={() => void facets.refetch()}
                >
                  {t('sales.orders.retrySearch')}
                </Button>
              </div>
            )}
          </div>
          <div className="bg-muted/30 text-muted-foreground flex min-h-10 shrink-0 items-center justify-between gap-2 border-b px-4 text-xs sm:px-5">
            <span role="status">
              {loading
                ? t('sales.orders.loadingProducts')
                : t('sales.orders.catalogResults', { shown: products.length, total })}
            </span>
            {filtered && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onKeyDown={(event) => event.stopPropagation()}
                onClick={resetFilters}
              >
                {t('sales.orders.resetFilters')}
              </Button>
            )}
          </div>
          <CommandList
            className="max-h-none min-h-0 flex-1"
            label={t('sales.orders.searchResults')}
            aria-busy={loading}
          >
            {loading ? (
              <div
                role="status"
                className="text-muted-foreground flex items-center justify-center gap-2 p-10 text-sm"
              >
                <Loader2 className="h-5 w-5 animate-spin" />
                {t('sales.orders.loadingProducts')}
              </div>
            ) : query.isError && !products.length ? (
              <div role="alert" className="space-y-3 p-8 text-center text-sm">
                <p>{t('sales.orders.productSearchError')}</p>
                <Button
                  type="button"
                  variant="outline"
                  onKeyDown={(event) => event.stopPropagation()}
                  onClick={() => void query.refetch()}
                >
                  {t('sales.orders.retrySearch')}
                </Button>
              </div>
            ) : !products.length ? (
              <div className="space-y-3 p-8 text-center">
                <SearchX className="text-muted-foreground mx-auto h-7 w-7" />
                <p className="text-sm font-medium">{t('products.noProducts')}</p>
                <p className="text-muted-foreground text-xs">
                  {t('sales.orders.catalogEmptyHint')}
                </p>
                {filtered && (
                  <Button
                    type="button"
                    variant="outline"
                    onKeyDown={(event) => event.stopPropagation()}
                    onClick={resetFilters}
                  >
                    {t('sales.orders.resetFilters')}
                  </Button>
                )}
              </div>
            ) : (
              <CommandGroup className="p-0">
                {products.map((product) => {
                  const quantity = items.find((item) => item.productId === product.id)?.quantity;
                  return (
                    <CommandItem
                      key={product.id}
                      value={product.id}
                      disabled={addingId !== null}
                      onSelect={() => void onAdd(product)}
                      onMouseDown={(event) => event.preventDefault()}
                      className="cursor-pointer gap-3 border-b px-4 py-3 sm:px-5"
                    >
                      <OrderProductIdentity product={product} quantity={quantity} />
                      <span className="flex shrink-0 items-center gap-1 text-xs font-medium">
                        {addingId === product.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Plus className="h-4 w-4" />
                        )}
                        {t(quantity ? 'sales.orders.addOneMore' : 'sales.orders.addProductShort')}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {query.hasNextPage && !loading && (
              <div className="p-3 text-center">
                {query.isFetchNextPageError && (
                  <p role="alert" className="text-destructive mb-2 text-xs">
                    {t('sales.orders.productSearchError')}
                  </p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  disabled={query.isFetchingNextPage}
                  onKeyDown={(event) => event.stopPropagation()}
                  onClick={() => void query.fetchNextPage()}
                >
                  {query.isFetchingNextPage && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t(
                    query.isFetchNextPageError
                      ? 'sales.orders.retrySearch'
                      : 'sales.orders.loadMoreProducts'
                  )}
                </Button>
              </div>
            )}
          </CommandList>
        </Command>
        <div className="bg-muted/20 flex shrink-0 items-center justify-between gap-3 border-t p-4 sm:px-5">
          <span role="status" className="sr-only">
            {announcement}
          </span>
          <p className="text-muted-foreground text-xs" role="status">
            {t('sales.orders.productsInOrder', {
              count: items.filter((item) => item.productId).length,
            })}
          </p>
          <Button type="button" onClick={() => onOpenChange(false)}>
            {t('sales.orders.backToOrder')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
